import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LangText } from '@/components'
import { preFixImgUrl } from '@/utils'
import classNames from 'classnames'
import { SearchOutlined } from '@ant-design/icons'
import { ConfigProvider, Empty, Pagination, Spin } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { SparkleTitle } from '../../HanedaShop'
import {
  apiCouponPage,
  CompanyBusinessTypeEnum,
  type CompanyBusinessTypeEnumCode,
  CouponSortTypeEnum,
  type MiniCouponResponse,
} from '@/service/api/couponPage'
import CouponCard from '../components/CouponCard'
import useModel, { type CouponQuickLinkItem } from './useModel';

const DEFAULT_PAGE_SIZE = 9;
const PAGE_SIZE_OPTIONS = [9, 18, 30, 60, 90] as const;

const CouponList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quickLinks } = useModel();
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [total, setTotal] = useState(0);
  const [sortType, setSortType] = useState<'default' | 'latest' | 'expiring'>('default');
  const [activeTab, setActiveTab] = useState<'' | CompanyBusinessTypeEnumCode>('');
  /** 已生效的搜索词（仅回车 / 点击搜索后更新，用于请求） */
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [coupons, setCoupons] = useState<MiniCouponResponse[]>([]);
  const controllerRef = useRef<AbortController>(new AbortController());
  /** 与接口请求同步的页码，避免筛选条件变更时闭包读到旧页 */
  const pageForApi = useRef(1);
  const [pageUi, setPageUi] = useState(1);
  const pageSizeRef = useRef(DEFAULT_PAGE_SIZE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // 滚动到锚点
  const scrollToCouponList = (e?: React.MouseEvent<HTMLDivElement>) => {
    e?.preventDefault();
    const element = document.getElementById('coupon-list');
    if (element) {
      const offset = 150;
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      window.scrollTo({
        top: rect.top + scrollTop - offset,
        behavior: 'instant'
      });
    }
  };

  const sortTypeToApi = useMemo(
    () =>
      ({
        default: CouponSortTypeEnum.DEFAULT,
        latest: CouponSortTypeEnum.LATEST,
        expiring: CouponSortTypeEnum.EXPIRE_SOON,
      }) as const,
    []
  )

  const fetchCoupons = (merchantNameOverride?: string) => {
    setLoading(true);
    controllerRef.current.abort();
    controllerRef.current = new AbortController();
    const merchantName =
      merchantNameOverride !== undefined
        ? merchantNameOverride.trim() || undefined
        : appliedKeyword.trim() || undefined;
    apiCouponPage(
      {
        current: pageForApi.current,
        size: pageSizeRef.current,
        companyBusinessType: activeTab || undefined,
        merchantName,
        sortType: sortTypeToApi[sortType],
      },
      {
        signal: controllerRef.current.signal,
      }
    ).then(res => {
      if (res.code === 0) {
        setCoupons(res.data.records || []);
        setTotal(res.data.total || 0);
        const startAnchor = document.getElementById('start-anchor');
        if (startAnchor) {
          const rect = startAnchor.getBoundingClientRect();
          const inViewport = rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;
          if (!inViewport) {
            scrollToCouponList()
          }
        }
      } else {
        setCoupons([]);
        setTotal(0);
      }
    }).finally(() => {
      setLoading(false);
    });
  }

  useEffect(() => {
    pageForApi.current = 1;
    setPageUi(1);
    fetchCoupons();
  }, [activeTab, sortType])

  const handleSearch = () => {
    const next = keyword.trim();
    pageForApi.current = 1;
    setPageUi(1);
    setAppliedKeyword(next);
    fetchCoupons(next);
  };

  const businessLngKey: Record<CompanyBusinessTypeEnumCode, string> = useMemo(
    () => ({
      [CompanyBusinessTypeEnum.DIGITAL_ELECTRONICS]: 'a4429b2b',
      [CompanyBusinessTypeEnum.FASHION_APPAREL]: '71bdcd98',
      [CompanyBusinessTypeEnum.FOOD_DINING]: '7c29670d',
      [CompanyBusinessTypeEnum.BEAUTY_SKINCARE]: '4bae91f8',
      [CompanyBusinessTypeEnum.TRAFFIC_TRAVEL]: 'c3d567d5',
      [CompanyBusinessTypeEnum.ACCOMMODATION_ENTERTAINMENT]: '92b0cd80',
    }),
    []
  )


  const tabOptions = useMemo(() => {
    const all = { nameKey: 'b0569342', value: '' as const }
    const rest = (
      Object.values(CompanyBusinessTypeEnum) as CompanyBusinessTypeEnumCode[]
    ).map(code => ({
      nameKey: businessLngKey[code],
      value: code,
    }))
    return [all, ...rest]
  }, [businessLngKey])

  const sortOptions = [
    { key: 'default', lngKey: 'a86f8c43' },
    { key: 'latest', lngKey: '0f3344ba' },
    { key: 'expiring', lngKey: '3d114f5b' },
  ] as const

  return (
    <div className="bg-[#F8F6FF]">
      {/* Hero（样式对齐 SagawaExp） */}
      <div
        className="relative min-h-[480px] flex flex-col items-center justify-center px-6 py-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${preFixImgUrl('f01726b69235efd3e50de29c1ae25c36.png')})` }}
      >
        <div className="flex relative z-10 flex-col items-center mx-auto max-w-3xl text-center">
          <LangText lngKey="cf14d89b" className="text-white text-[40px] md:text-[44px] font-bold leading-tight px-2" />
          <LangText lngKey="075dace4" className="text-white text-[17px] md:text-[18px] leading-relaxed mt-6 px-2" />
          <div className="flex flex-wrap gap-5 justify-center mt-10">
            <div
              className="h-[48px] min-w-[140px] px-10 flex items-center justify-center rounded-full text-white text-[17px] cursor-pointer transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(270deg, #A75AFF 0%, #4B51FF 100%)' }}
              onClick={() => navigate('/mine-coupon')}
            >
              <LangText lngKey="65d24316" className="text-white" />
            </div>
          </div>
        </div>
      </div>

      <section className="pt-16">
        <div className="px-6 mx-auto w-8xl md:px-10">
          <SparkleTitle lngKey="89188052" purpleTitle />
        </div>
      </section>

      <div className='w-8xl mx-auto px-10  py-[40px]' id='coupon-list'>
        <div className='sticky top-20 z-30 bg-white rounded-[8px] p-4 pb-0 shadow-[0_2px_20px_0_rgba(30,34,46,0.05)]'>
          <div className='flex flex-wrap gap-4 justify-between items-center'>
            <div className='flex flex-wrap gap-2 items-center'>
              {
                tabOptions.map(option => (
                  <div
                    key={option.value || 'all'}
                    className={classNames(
                      'h-8 px-4 rounded-[20px] text-[15px] flex min-w-[70px] items-center justify-center cursor-pointer transition-colors',
                      activeTab === option.value ? 'text-[#5751FF] bg-[#EFEFFF]' : 'text-[#333333] bg-[#F5F5F5]'
                    )}
                    onClick={() => {
                      pageForApi.current = 1;
                      setPageUi(1);
                      setActiveTab(option.value);
                    }}
                  >
                    <LangText lngKey={option.nameKey} />
                  </div>
                ))
              }
            </div>
            <div
              className={classNames(
                'flex h-10 min-w-[280px] w-full max-w-[420px] items-stretch shrink-0 overflow-hidden rounded-full border-[2px] border-[#7B66FF] bg-white'
              )}
            >
              <span className='flex shrink-0 items-center pl-3 text-[#333333]'>
                <SearchOutlined className='text-[16px]' />
              </span>
              <input
                type='search'
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                className='min-w-0 flex-1 border-0 bg-transparent px-2 text-sm text-[#333333] outline-none placeholder:text-[#999]'
                placeholder={t('28f8af4f')}
                enterKeyHint='search'
              />
              <button
                type='button'
                onClick={handleSearch}
                className='shrink-0 rounded-full cursor-pointer border-0 bg-linear-to-r from-[#9C59FF] to-[#5751FF] px-5 text-sm font-medium text-white transition-opacity hover:opacity-95 active:opacity-90'
              >
                <LangText lngKey='25242fc9' className='text-white' />
              </button>
            </div>
          </div>

          <div className='mt-5 flex w-full items-end gap-10 border-b border-[#eeeeee] bg-white px-1'>
            {
              sortOptions.map(item => (
                <div
                  key={item.key}
                  role='button'
                  tabIndex={0}
                  className={classNames(
                    'pb-3 text-[15px] transition-colors ml-2',
                    sortType === item.key
                      ? 'border-b-2 border-[#5751FF] font-medium text-[#5751FF]'
                      : 'border-b-2 border-transparent text-[#333333] hover:text-[#555555]'
                  )}
                  onClick={() => setSortType(item.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSortType(item.key);
                    }
                  }}
                >
                  <LangText lngKey={item.lngKey} />
                </div>
              ))
            }
          </div>
        </div>

        <div className='mt-4'>
          {
            loading ? (
              <div className='h-[240px] flex items-center justify-center'>
                <Spin />
              </div>
            ) : coupons.length ? (
              <div className='grid grid-cols-3 gap-4'>
                {
                  coupons.map(coupon => {
                    return (
                      <CouponCard
                        key={coupon.couponId}
                        coupon={coupon}
                        onReceived={fetchCoupons}
                      />
                    )
                  })
                }
              </div>
            ) : (
              <div className='h-[240px] flex items-center justify-center'>
                <Empty description={t('337e26e6')} />
              </div>
            )
          }
        </div>

        {
          total > 0 ? (
            <div
              className={classNames(
                'mt-10 flex justify-center',
                // 与设计图一致：总数 → 每页条数 → 上一页 → 页码 → 下一页 → 跳转
                '[&_.ant-pagination]:flex [&_.ant-pagination]:flex-wrap [&_.ant-pagination]:items-center [&_.ant-pagination]:gap-2',
                '[&_.ant-pagination-total-text]:order-1 [&_.ant-pagination-total-text]:mr-0 [&_.ant-pagination-total-text]:text-[#666666] [&_.ant-pagination-total-text]:text-sm',
                '[&_.ant-pagination-options]:order-2 [&_.ant-pagination-options]:!ml-0 [&_.ant-pagination-options]:flex [&_.ant-pagination-options]:items-center [&_.ant-pagination-options]:gap-2',
                '[&_.ant-pagination-prev]:order-3',
                '[&_.ant-pagination-item]:order-4',
                '[&_.ant-pagination-jump-prev]:order-4',
                '[&_.ant-pagination-jump-next]:order-4',
                '[&_.ant-pagination-next]:order-5',
                // 非当前页：浅灰描边
                '[&_.ant-pagination-item:not(.ant-pagination-item-active)]:border-[#E5E5E5] [&_.ant-pagination-item:not(.ant-pagination-item-active)]:bg-white',
                '[&_.ant-pagination-prev_.ant-pagination-item-link]:border-[#E5E5E5] [&_.ant-pagination-next_.ant-pagination-item-link]:border-[#E5E5E5]',
                '[&_.ant-pagination-options-size-changer_.ant-select-selector]:!rounded-md [&_.ant-pagination-options-size-changer_.ant-select-selector]:!border-[#E5E5E5]'
              )}
            >
              <ConfigProvider
                theme={{
                  components: {
                    Pagination: {
                      colorPrimary: '#5A55FF',
                      itemActiveBg: '#5A55FF',
                      itemActiveColor: '#ffffff',
                      itemActiveColorHover: '#ffffff',
                      borderRadius: 8,
                    },
                  },
                }}
              >
                <Pagination
                  align='start'
                  current={pageUi}
                  pageSize={pageSize}
                  total={total}
                  showSizeChanger
                  pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
                  showQuickJumper
                  showTotal={count => t('731ecff6', { count })}
                  locale={{
                    items_per_page: t('14b34dd7'),
                    jump_to: t('e1e91067'),
                    page: t('ca53d4e3'),
                  }}
                  onChange={(page, nextSize) => {
                    const sizeChanged = nextSize !== pageSizeRef.current;
                    if (sizeChanged) {
                      pageSizeRef.current = nextSize;
                      setPageSize(nextSize);
                      pageForApi.current = 1;
                      setPageUi(1);
                    } else {
                      pageForApi.current = page;
                      setPageUi(page);
                    }
                    fetchCoupons();
                  }}
                />
              </ConfigProvider>
            </div>
          ) : null
        }
      </div>
      <div
          className="relative min-h-[360px] flex flex-col items-center justify-center px-6 py-16 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${preFixImgUrl('2a9fda00bf273aa46cd435ba98d345e9.png')})` }}
        >
          <div className="flex relative z-10 flex-col items-center max-w-3xl text-center">
            {/* <OutLink platform={PlatformEnum.VIP}> */}
              <div
                className="h-[52px] min-w-[200px] px-10 flex items-center justify-center rounded-full text-white text-[18px] cursor-pointer transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(270deg, #A75AFF 0%, #4B51FF 100%)' }}
                onClick={() => navigate('/mine-coupon')}
              >
                <LangText lngKey="4696e630">{t('4696e630')}</LangText>
              </div>
            {/* </OutLink> */}
          </div>
        </div>

        {/* 快捷入口：用户条款 /items，注意事项 /coupon-precautions，联系我们 mailto */}
        <section className="pt-12 pb-16">
          <div className="px-6 mx-auto w-8xl md:px-10">
            <div className="grid grid-cols-3 gap-3 w-full sm:gap-4 md:gap-6">
              {quickLinks.map((item: CouponQuickLinkItem) => {
                const cardClass =
                  'flex flex-col gap-4 items-center min-w-0 text-center cursor-pointer transition-opacity hover:opacity-90 text-inherit no-underline';
                const inner = (
                  <>
                    <span className="w-[88px] h-[88px] flex items-center justify-center">
                      <img src={item.iconImg} alt="" className="object-contain max-w-full max-h-full" />
                    </span>
                    <LangText lngKey={item.key} className="text-[18px] text-[#333333] font-bold">
                      {t(item.key)}
                    </LangText>
                  </>
                );
                if ('href' in item) {
                  return (
                    <a key={item.key} href={item.href} className={cardClass}>
                      {inner}
                    </a>
                  );
                }
                return (
                  <Link key={item.key} to={item.to} className={cardClass}>
                    {inner}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
    </div>
  )
}

export default CouponList